import type { MappedSearchParams } from './TravelProductToggle.types';
import { CAR_QUERY_PARAM_KEYS, HOTEL_LOCATION_QUERY_PARAM_KEYS } from '@shared/carRental/CarRentalConsole.constants';

// Reads a query param, returning undefined for empty or whitespace-only values.
const read = (params: URLSearchParams, key: string): string | undefined => {
  const value = params.get(key)?.trim();
  return value ? value : undefined;
};

// Returns the first non-empty value across a list of query param keys.
const readFirst = (params: URLSearchParams, ...keys: ReadonlyArray<string>): string | undefined => {
  for (const key of keys) {
    const value = read(params, key);
    if (value) {
      return value;
    }
  }
  return undefined;
};

// Removes undefined entries from the mapped search params object.
const compact = (mapped: Readonly<Record<keyof MappedSearchParams, string | undefined>>): MappedSearchParams => {
  return Object.entries(mapped).reduce<Record<string, string>>((acc, [key, value]) => {
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

// Maps hotel search params to car search params, transferring location and dates only.
export const mapHotelSearchToCarSearchParams = (hotelParams: URLSearchParams): MappedSearchParams => {
  return compact({
    checkin: read(hotelParams, CAR_QUERY_PARAM_KEYS.checkin),
    checkout: read(hotelParams, CAR_QUERY_PARAM_KEYS.checkout),
    lat: read(hotelParams, HOTEL_LOCATION_QUERY_PARAM_KEYS.lat),
    lng: read(hotelParams, HOTEL_LOCATION_QUERY_PARAM_KEYS.lng),
    destination: read(hotelParams, HOTEL_LOCATION_QUERY_PARAM_KEYS.destination),
    googlePlaceId: read(hotelParams, HOTEL_LOCATION_QUERY_PARAM_KEYS.googlePlaceId),
  });
};

// Maps car search params to hotel search params, transferring location and dates only.
export const mapCarSearchToHotelSearchParams = (carParams: URLSearchParams): MappedSearchParams => {
  return compact({
    checkin: read(carParams, CAR_QUERY_PARAM_KEYS.checkin),
    checkout: read(carParams, CAR_QUERY_PARAM_KEYS.checkout),
    lat: readFirst(carParams, CAR_QUERY_PARAM_KEYS.pickupLat, HOTEL_LOCATION_QUERY_PARAM_KEYS.lat),
    lng: readFirst(carParams, CAR_QUERY_PARAM_KEYS.pickupLng, HOTEL_LOCATION_QUERY_PARAM_KEYS.lng),
    destination: readFirst(carParams, CAR_QUERY_PARAM_KEYS.pickupDestination, HOTEL_LOCATION_QUERY_PARAM_KEYS.destination),
    googlePlaceId: readFirst(carParams, CAR_QUERY_PARAM_KEYS.pickupPlaceId, HOTEL_LOCATION_QUERY_PARAM_KEYS.googlePlaceId),
  });
};
